import { AuthResult } from "./types"
import { updateProfile, User } from "firebase/auth"
import { doc, setDoc, updateDoc } from "firebase/firestore"
import { MemberPermission } from "@/components/portfolio/settings/types/memberTypes"
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore"
import {
  getAuth,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
} from "firebase/auth"
import { db } from "@/utils/firebase"
import { addUserToPortfolio } from "@/utils/firestore/users"
import { updateCurrentUser } from "@/utils/firestore/users/userOperations"
import { addUserToBusiness } from "@/utils/firestore/business/businessOperations"

/**
 * Add a user directly to a portfolio or business
 * @param email User's email
 * @param firstName User's first name
 * @param lastName User's last name
 * @param role User's role in the portfolio/business
 * @param entityId Portfolio ID or Business ID depending on the source
 * @param permissions User's permissions
 * @param phone User's phone number
 * @param source Whether to add to a portfolio or business
 * @returns Result of the operation
 */
export const addUser = async (
  email: string,
  firstName: string = "",
  lastName: string = "",
  role: string = "",
  entityId: string = "",
  permissions: MemberPermission[] = [],
  phone: string = "",
  source: "portfolio" | "business"
): Promise<AuthResult> => {
  try {
    const auth = getAuth()
    const currentUser = auth.currentUser

    // Check if user exists in Firestore by email
    const usersRef = collection(db, "users")
    const userQuery = query(usersRef, where("email", "==", email))
    const userSnapshot = await getDocs(userQuery)
    const userExistsInFirestore = !userSnapshot.empty

    console.log("User exists in firestore", userExistsInFirestore)

    // Handle based on source (portfolio or business)
    if (source === "portfolio") {
      // Check if user exists in Firebase Auth
      const methods = await fetchSignInMethodsForEmail(auth, email)
      const userExists = methods && methods.length > 0

      // Handle new user creation only if user doesn't exist in both Auth and Firestore
      if (!userExists && !userExistsInFirestore) {
        console.log("Creating new user for portfolio")
        const tempPassword =
          Math.random().toString(36).slice(-10) +
          Math.random().toString(36).slice(-10).toUpperCase()

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          tempPassword
        )
        const newUser = userCredential.user

        await updateCurrentUser({
          id: newUser.uid,
          userId: newUser.uid,
          email,
          firstName,
          lastName,
          phone,
          avatarInitials:
            firstName && lastName
              ? `${firstName[0]}${lastName[0]}`
              : email.substring(0, 2).toUpperCase(),
          portfolios: [entityId],
        })

        // Directly add user to portfolio
        await addUserToPortfolio(
          newUser.uid,
          entityId,
          role,
          permissions,
          firstName,
          lastName,
          email,
          phone
        )

        await auth.signOut()

        if (currentUser) {
          await auth.updateCurrentUser(currentUser)
        }

        return {
          success: true,
          message: "User created and added to portfolio",
        }
      }

      // Handle existing user
      if (userExistsInFirestore) {
        const userData = userSnapshot.docs[0].data()
        const userId = userData.id

        // Directly add user to portfolio
        await addUserToPortfolio(
          userId,
          entityId,
          role,
          permissions,
          firstName || userData.firstName,
          lastName || userData.lastName,
          email,
          phone || userData.phone
        )

        return {
          success: true,
          message: "Existing user added to portfolio",
        }
      }

      return {
        success: true,
        message: "User added to portfolio",
      }
    } else if (source === "business") {
      // Business source - add user directly to the business
      // Check if user exists in Firebase Auth
      const methods = await fetchSignInMethodsForEmail(auth, email)
      const userExists = methods && methods.length > 0

      // Handle new user creation only if user doesn't exist in both Auth and Firestore
      if (!userExists && !userExistsInFirestore) {
        console.log("Creating new user for business")
        const tempPassword =
          Math.random().toString(36).slice(-10) +
          Math.random().toString(36).slice(-10).toUpperCase()

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          tempPassword
        )
        const newUser = userCredential.user

        await updateCurrentUser({
          id: newUser.uid,
          userId: newUser.uid,
          email,
          firstName,
          lastName,
          phone,
          avatarInitials:
            firstName && lastName
              ? `${firstName[0]}${lastName[0]}`
              : email.substring(0, 2).toUpperCase(),
        })

        // Directly add user to the specific business
        await addUserToBusiness(
          newUser.uid,
          entityId,
          role,
          permissions,
          firstName,
          lastName,
          email,
          phone
        )

        await auth.signOut()

        if (currentUser) {
          await auth.updateCurrentUser(currentUser)
        }

        return {
          success: true,
          message: "User created and added to business",
        }
      }

      // Handle existing user
      if (userExistsInFirestore) {
        const userData = userSnapshot.docs[0].data()
        const userId = userData.id

        // Directly add user to the specific business
        await addUserToBusiness(
          userId,
          entityId,
          role,
          permissions,
          firstName || userData.firstName,
          lastName || userData.lastName,
          email,
          phone || userData.phone
        )

        return {
          success: true,
          message: "Existing user added to business",
        }
      }

      return {
        success: true,
        message: "User added to business",
      }
    }

    // Default return if source is neither portfolio nor business
    return {
      success: false,
      error: "Invalid source specified",
    }
  } catch (error: unknown) {
    console.error("User add error:", error)

    // Handle auth/email-already-in-use error
    const firebaseError = error as { code?: string }
    if (firebaseError.code === "auth/email-already-in-use") {
      try {
        const auth = getAuth()
        // Check if user exists in Firestore
        const usersRef = collection(db, "users")
        const userQuery = query(usersRef, where("email", "==", email))
        const userSnapshot = await getDocs(userQuery)

        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data()
          
          if (source === "portfolio") {
            await addUserToPortfolio(
              userData.id,
              entityId,
              role,
              permissions,
              firstName || userData.firstName,
              lastName || userData.lastName,
              email,
              phone || userData.phone
            )
          } else if (source === "business") {
            await addUserToBusiness(
              userData.id,
              entityId,
              role,
              permissions,
              firstName || userData.firstName,
              lastName || userData.lastName,
              email,
              phone || userData.phone
            )
          }

          return {
            success: true,
            message: source === "portfolio" 
              ? "Existing user added to portfolio" 
              : "Existing user added to business",
          }
        }

        return {
          success: true,
          message: source === "portfolio"
            ? "User added to portfolio"
            : "User added to business",
        }
      } catch (innerError) {
        console.error("Error handling existing user:", innerError)
      }
    }

    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to add user",
    }
  }
}

// For backward compatibility
export const sendUserInvite = addUser;
export const addUserDirectly = addUser;

/**
 * Update a user's status
 * This function is kept for backward compatibility
 * @param email The email of the user to update
 * @param status The new status
 * @returns Whether the update was successful
 */
export const updateInvitationStatus = async (
  email: string,
  status: string
): Promise<boolean> => {
  try {
    console.log(`Updating user status for email: ${email} to ${status}`);

    // Check Firestore for user with this email
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      await updateDoc(userDoc.ref, { status });
      console.log(`Successfully updated status for ${email}`);
      return true;
    }

    console.log(`No user found for email: ${email}`);
    return false;
  } catch (error) {
    console.error("Error updating user status:", error);
    return false;
  }
};

/**
 * Resend an invitation to a user
 * This function is kept for backward compatibility
 * @param member The member to resend the invitation to
 * @param entityId The portfolio or business ID
 * @param source Whether to add to a portfolio or business
 * @returns The result of the operation
 */
export const resendInvitation = async (
  member: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    phone?: string;
    permissions?: unknown[];
  },
  entityId: string,
  source: "portfolio" | "business" = "portfolio"
): Promise<AuthResult> => {
  try {
    // Use the addUser function to add the user again
    return await addUser(
      member.email,
      member.firstName,
      member.lastName,
      member.role,
      entityId,
      member.permissions as MemberPermission[] || [],
      member.phone || "",
      source
    );
  } catch (error) {
    console.error("Error resending invitation:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to resend invitation";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get invitation data for a specific email
 * This function is kept for backward compatibility
 * @param email The email to get invitation data for
 * @param status Optional status filter
 * @returns The invitation data or null if not found
 */
// Define a type for the invitation data
interface InvitationData {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  portfolioId?: string;
  permissions?: unknown[];
  status?: string;
  invitedAt?: string;
  [key: string]: unknown;
}

export const getInvitationByEmail = async (
  email: string,
  status?: string
): Promise<InvitationData | null> => {
  try {
    console.log(`Fetching user data for email: ${email}`);

    // Check Firestore for user with this email
    const usersRef = collection(db, "users");
    let q = query(usersRef, where("email", "==", email));

    // Conditionally filter by status if provided
    if (status) {
      q = query(q, where("status", "==", status));
    }

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      console.log("Found user in Firestore:", userData);
      
      // Return user data in a format compatible with the old invitation data
      return {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        role: userData.role,
        portfolioId: userData.portfolioId,
        permissions: userData.permissions,
        status: userData.status || "active",
      };
    }

    console.log(
      "No user found for email:",
      email,
      status ? `with status: ${status}` : ""
    );
    return null;
  } catch (error) {
    console.error("Error fetching user data:", error);
    return null;
  }
};

/**
 * Update a user's profile in Firebase Auth
 * @param user The user to update
 * @param displayName The display name to set
 * @returns A promise that resolves when the profile is updated
 */
export const updateUserProfile = async (
  user: User,
  displayName: string
): Promise<void> => {
  try {
    await updateProfile(user, { displayName });
    console.log(`Updated profile for user ${user.uid} with display name ${displayName}`);
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

/**
 * Store user data in Firestore
 * @param userId The ID of the user to store data for
 * @param userData The data to store
 * @returns A promise that resolves when the data is stored
 */
export const storeUserData = async (
  userId: string,
  userData: Record<string, unknown>
): Promise<void> => {
  try {
    const userRef = doc(db, "users", userId);
    await setDoc(userRef, {
      ...userData,
      id: userId,
      userId: userId,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    
    console.log(`Stored user data for user ${userId}`);
  } catch (error) {
    console.error("Error storing user data:", error);
    throw error;
  }
};
